<template>
  <div>
    <fieldset class="lib__form__fieldset" v-if="isTracking">
      <legend class="lib__form__legend">{{ $t('complaint.complaintInfo.tracking.numberFile') }}
        <sup class="lib__tooltips lib__tooltips--large" style="margin-left: 5px;">
          <span class="lib__tooltips__trigger">
            <i class="fas fa-info"></i>
          </span>
          <span class="lib__tooltips__data" v-html="$t('complaint.complaintInfo.tracking.numberFileTooltip')"></span>
        </sup>
      </legend>
      <text-box :rules="{ 'complaint-number-file': true, 'required': true }" v-model="value.fileNumber"></text-box>
    </fieldset>

    <fieldset class="lib__form__fieldset" v-if="isIndemnityRequest">
      <legend class="lib__form__legend">{{ $t('complaint.complaintInfo.sinisterInfo') }}</legend>
      <div class="row">
        <div class="col-xs-12 col-sm-12 col-md-12" v-if="damageTypeVisible">
          <ValidationProvider rules="required" slim v-slot="{ errors, passed, failed }">
            <div :class="[failed && 'lib__field--error', passed && 'lib__field--valid']" class="lib__field">
              <label class="lib__field__label">{{ $t('complaint.complaintInfo.damageType') }}</label>
              <div class="row">
                <div class="col-xs-12 col-md-6">
                  <TileRadio item="corporal" :label="$t('common.damageType.corporal')" group="damageType" v-model="value.damageType">
                  </TileRadio>
                </div>

                <div class="col-xs-12 col-md-6">
                  <TileRadio item="material" :label="$t('common.damageType.material')" group="damageType" v-model="value.damageType">
                  </TileRadio>
                </div>
              </div>
              <error-message :error="errors[0]"></error-message>
            </div>
          </ValidationProvider>
        </div>
        <div class="col-xs-12 col-sm-12 col-md-12">
          <DateBox :max="maxDate" tileAlignement :label="$t('complaint.complaintInfo.sinisterDate')" required v-model="value.sinisterDate"></DateBox>
        </div>
      </div>
    </fieldset>

    <fieldset class="lib__form__fieldset">
      <ValidationObserver ref="validator" slim>
        <legend class="lib__form__legend">{{ isTracking ? $t('complaint.complaintInfo.tracking.question') : $t('complaint.complaintInfo.description') }}</legend>
        <span class="lib__field__note">{{ isTracking ? $t('complaint.complaintInfo.tracking.questionDescription') : $t('complaint.complaintInfo.noteDescription') }}</span>
        <CheckBox v-model="value.isIndemnitySendToLegal" :label="$t('complaint.complaintInfo.isIndemnityCheck')" v-if="isIndemnitySendToLegal" />
        <ValidationProvider :rules="{ required: true }" slim v-slot="{ errors, passed, failed }">
          <div :class="[failed && 'lib__field--error', passed && (value.description !== null && value.description !== '') && 'lib__field--valid']" class="lib__field">
            <textarea class="lib__field__textarea" data-vv-as="description" name="complaint-description" rows="5" v-model="value.description"></textarea>
            <error-message :error="errors[0]"></error-message>
          </div>
          <DateBox v-if="isSinisterDateVisible" :max="maxDate" tileAlignement :label="$t('complaint.complaintInfo.sinisterDate')" required v-model="value.sinisterDate"></DateBox>
        </ValidationProvider>
      </ValidationObserver>
    </fieldset>

    <fieldset class="lib__form__fieldset" v-if="isIndemnityRequest">
      <legend class="lib__form__legend">
        {{ $t('complaint.complaintInfo.bankAccountInformation') }}
        <sup class="lib__tooltips lib__tooltips--large" style="margin-left: 5px">
          <span class="lib__tooltips__trigger">
            <i class="fas fa-info"></i>
          </span>
          <span class="lib__tooltips__data">{{ $t('complaint.complaintInfo.bankAccountInformationMessage') }}</span>
        </sup>
      </legend>

      <div class="row">
        <div class="col-xs-12 col-md-6">
          <text-box :label="$t('complaint.complaintInfo.iban')" iban required :rules="{'required': true, 'alpha_num': true}" v-model="value.bankAccountIban"></text-box>
        </div>
        <div class="col-xs-12 col-md-6" v-if="bicVisible">
          <masked-text-box :label="$t('complaint.complaintInfo.bic')" mask="SSSSSSXXXXX" pattern="[A-Z,a-z]{6}[A-Z,a-z,0-9]{5}" required v-model="value.bankAccountBic"></masked-text-box>
        </div>
      </div>
    </fieldset>

    <fieldset class="lib__form__fieldset" v-if="isTracking">
      <legend class="lib__form__legend">{{ $t('common.contact.form.email') }}</legend>
      <text-box label="" data-cy="contact-form-email" email required v-model="value.email"></text-box>
    </fieldset>
  </div>
</template>

<script lang="ts">
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { CategorySelectionStepModel, ComplaintInfo, EnergySelectionStepModel } from '@/models';
import { Getter, State } from 'vuex-class';
import { complaintService, dateService } from '@/services';

@Component
export default class ComplaintInfoForm extends Vue {
  //#region [Property]
  @Prop({ type: Object, required: true }) public readonly value!: ComplaintInfo;
  @Prop({ type: Boolean, required: true }) public readonly isElectricity!: boolean;
  //#endregion

  //#region [Data]
  @State('category', { namespace: 'complaint/categorySelection' })
  public categorySelectionState!: CategorySelectionStepModel | null;
  @State('newOrTracking', { namespace: 'complaint/newOrTracking' })
  public readonly newOrTrackingState!: string;
  @State('energySelectionStepInfo', { namespace: 'complaint/energy' })
  public energySelectionInfoState!: EnergySelectionStepModel | null
  @Getter('isIndemnityRequest', { namespace: 'complaint/categorySelection' })
  public readonly isIndemnityRequest!: boolean;
  @Getter('isIndemnitySendToLegal', { namespace: 'complaint/categorySelection' })
  public readonly isIndemnitySendToLegal!: boolean;
  //#endregion

  //#region [Computed]
  public get maxDate(): string {
    return dateService.stringifyDate(new Date())
  }
  public get isTracking() {
    return this.newOrTrackingState === 'tracking';
  }

  public get damageTypeVisible(): boolean {
    return complaintService.isInList(this.categorySelectionState, complaintService.damageTypeRequiredList)
  }

  public get bicVisible(): boolean {
    return (
      this.value.bankAccountIban !== null &&
      this.value.bankAccountIban.length > 1 &&
      !this.value.bankAccountIban.toLowerCase().startsWith('be')
    )
  }

  public get isSinisterDateVisible() {
    const isIndemnitySendToLegalCondition =
      complaintService.isInList(this.categorySelectionState, complaintService.indemnitySendToLegal) &&
      this.value.isIndemnitySendToLegal

    const isSinisterDateMustBeDeleted =
      !isIndemnitySendToLegalCondition ||
      !complaintService.isInList(this.categorySelectionState, complaintService.indemnityRequestList)

    if (isIndemnitySendToLegalCondition) {
      return true
    }

    if (isSinisterDateMustBeDeleted) {
      this.value.sinisterDate = null
    }

    return false
  }
  //#endregion

  //#region [Watch]
  @Watch('value', {deep: true})
  public onValueChanged(newVal: ComplaintInfo) {
    this.initValidation();
  }

  @Watch('value.isIndemnitySendToLegal')
  public isIndemnitySendToLegalChanged() {
    if (!this.value.isIndemnitySendToLegal) {
      this.value.sinisterDate = null
    }
  }
  //#endregion

  //#region [Method]
  public mounted() {
    this.initValidation();
  }

  public initValidation() {
    if (this.$validatorObs) {
      const flags = this.$validatorObs.flags;
      if (!this.$validatorObs.flags.validated && this.value.description !== null && this.value.description !== '') {
        this.$nextTick(() => {
          this.$validatorObs.validate();
        });
      }
    }
  }
  //#endregion
}
</script>